import type { PlanChange, PlanDisplayReference, PlanEntry } from '../../lib/types';

export interface EntryPresentation {
  title: string;
  version: string | null;
  changes: readonly PlanChange[];
}

export interface BomComponent {
  /** Preserve the original entry and its decision ID, including unchanged rows. */
  entry: PlanEntry;
  componentName: string;
  componentVersion: string | null;
  changes: readonly PlanChange[];
}

export interface BomGroup {
  key: string;
  productName: string;
  productVersion: string | null;
  components: readonly BomComponent[];
}

export interface PlanPresentation {
  byEntryId: ReadonlyMap<string, EntryPresentation>;
  bomGroups: readonly BomGroup[];
}

const REFERENCE_CLASSES: Readonly<Record<string, string>> = {
  ProduktTypeID: 'ProduktType',
  ProduktVersionID: 'ProduktVersion',
  AktuelleVersionID: 'ProduktVersion',
  AusgelieferteVersionID: 'ProduktVersion',
  VorgaengerVersionID: 'ProduktVersion',
  KomponenteTypeID: 'KomponenteType',
  KomponenteVersionID: 'KomponenteVersion',
  KundeID: 'Kunde',
  ProduktInstanzID: 'ProduktInstanz',
  CveID: 'CVE',
};

const FIELD_LABELS: Readonly<Record<string, string>> = {
  ProduktTypeID: 'Produkt',
  ProduktVersionID: 'Produktversion',
  AktuelleVersionID: 'Aktuelle Produktversion',
  AusgelieferteVersionID: 'Ausgelieferte Produktversion',
  VorgaengerVersionID: 'Vorgängerversion',
  KomponenteTypeID: 'Komponente',
  KomponenteVersionID: 'Komponentenversion',
  KundeID: 'Kunde',
  ProduktInstanzID: 'Produktinstanz',
  CveID: 'CVE',
  VersionNr: 'Version',
  ReleaseDatum: 'Veröffentlichung',
  Aenderungsbeschreibung: 'Änderungsbeschreibung',
  AuslieferDatum: 'Auslieferdatum',
  AnsprechpartnerEmail: 'Kontakt-E-Mail',
  IstEuKunde: 'EU-Kunde',
  AktivAusgenutzt: 'Aktiv ausgenutzt',
  BaseScore: 'CVSS-Basiswert',
  CveId: 'CVE',
  ErfasstAm: 'Erfasst am',
  SwEdition: 'Software-Edition',
  TargetSw: 'Zielsoftware',
  TargetHw: 'Zielhardware',
};

const BOOLEAN_FIELDS = new Set(['IstEuKunde', 'AktivAusgenutzt']);

/** Create refs are wrapped; updates use bare natural keys. */
function referenceKey(value: string, klass: string): string {
  const prefix = `${klass} »`;
  return value.startsWith(prefix) && value.endsWith('«')
    ? value.slice(prefix.length, -1)
    : value;
}

function changedValue(entry: PlanEntry, field: string): string | undefined {
  return entry.changes.find((change) => change.field === field)?.new;
}

function changedReference(entry: PlanEntry, field: string): string | undefined {
  const value = changedValue(entry, field);
  const klass = REFERENCE_CLASSES[field];
  return value === undefined || klass === undefined ? undefined : referenceKey(value, klass);
}

function referenceLabel(reference: PlanDisplayReference): string {
  return reference.version ? `${reference.name} · Version ${reference.version}` : reference.name;
}

/**
 * Present the whole plan before applying visibility filters. Unchanged catalog
 * rows still provide identity information, and must not lose their membership.
 * Structured API metadata is authoritative. Legacy keys are only a fallback.
 */
export function buildPlanPresentation(entries: readonly PlanEntry[]): PlanPresentation {
  const byClass = new Map<string, Map<string, PlanEntry>>();
  for (const entry of entries) {
    const catalog = byClass.get(entry.klass) ?? new Map<string, PlanEntry>();
    catalog.set(entry.key, entry);
    byClass.set(entry.klass, catalog);
  }

  function resolve(klass: string, key: string): PlanDisplayReference {
    const entry = byClass.get(klass)?.get(key);
    if (entry?.display?.name) {
      return { key, name: entry.display.name, version: entry.display.version || undefined };
    }

    const productVersion = klass === 'ProduktVersion';
    const componentVersion = klass === 'KomponenteVersion';
    if (!productVersion && !componentVersion) {
      return { key, name: entry === undefined ? key : changedValue(entry, 'Bezeichnung') ?? changedValue(entry, 'Name') ?? key };
    }

    const versionField = productVersion ? 'VersionNr' : 'Version';
    const parentField = productVersion ? 'ProduktTypeID' : 'KomponenteTypeID';
    const parentClass = productVersion ? 'ProduktType' : 'KomponenteType';
    const delimiter = productVersion ? ':V' : ':';
    let version = entry === undefined ? undefined : changedValue(entry, versionField);
    let name = entry === undefined ? undefined : changedReference(entry, parentField);
    name ??= entry === undefined ? undefined : changedValue(entry, 'Bezeichnung');

    // Exact parent identities allow + and : in both names and version strings.
    if (name === undefined) {
      const parents = [...(byClass.get(parentClass)?.keys() ?? [])]
        .filter((parent) => key.startsWith(`${parent}${delimiter}`))
        .sort((a, b) => b.length - a.length);
      name = parents[0];
    }
    if (name !== undefined && version === undefined && key.startsWith(`${name}${delimiter}`)) {
      version = key.slice(name.length + delimiter.length);
    }
    if (name === undefined && version !== undefined && key.endsWith(`${delimiter}${version}`)) {
      name = key.slice(0, -delimiter.length - version.length);
    }

    // Ambiguous legacy keys remain visible verbatim instead of guessing at a
    // name/version split. Current plans provide metadata even for unchanged rows.
    if (name === undefined) {
      const at = key.indexOf(delimiter);
      if (at > 0 && at === key.lastIndexOf(delimiter) && at + delimiter.length < key.length) {
        name = key.slice(0, at);
        version ??= key.slice(at + delimiter.length);
      }
    }
    return { key, name: name ?? key, version };
  }

  function presentChange(entry: PlanEntry, change: PlanChange): PlanChange {
    function valueLabel(value: string | null): string | null {
      if (value === null) return null;
      const klass = REFERENCE_CLASSES[change.field];
      if (klass !== undefined) {
        const key = referenceKey(value, klass);
        const references = entry.display?.references;
        return referenceLabel(references?.[`${klass}:${key}`] ?? references?.[key] ?? resolve(klass, key));
      }
      if (BOOLEAN_FIELDS.has(change.field)) {
        if (value.toLowerCase() === 'true') return 'Ja';
        if (value.toLowerCase() === 'false') return 'Nein';
      }
      return value;
    }
    return {
      field: FIELD_LABELS[change.field] ?? change.field,
      old: valueLabel(change.old),
      new: valueLabel(change.new) ?? '',
    };
  }

  function bomReferences(entry: PlanEntry): { product?: PlanDisplayReference; component?: PlanDisplayReference } {
    let productKey = entry.display?.product?.key ?? changedReference(entry, 'ProduktVersionID');
    let componentKey = entry.display?.component?.key ?? changedReference(entry, 'KomponenteVersionID');

    if (productKey === undefined || componentKey === undefined) {
      // Match complete catalog keys. Splitting blindly at '+' breaks names and
      // build versions such as "Gateway+Pro" and "3.2+build:7".
      const candidates: { product: string; component: string; score: number }[] = [];
      for (let at = entry.key.indexOf('+'); at >= 0; at = entry.key.indexOf('+', at + 1)) {
        const product = entry.key.slice(0, at);
        const component = entry.key.slice(at + 1);
        if (!product || !component) continue;
        if (productKey !== undefined && product !== productKey) continue;
        if (componentKey !== undefined && component !== componentKey) continue;
        const score = Number(byClass.get('ProduktVersion')?.has(product) || product === productKey)
          + Number(byClass.get('KomponenteVersion')?.has(component) || component === componentKey);
        if (score > 0) candidates.push({ product, component, score });
      }
      const bestScore = Math.max(0, ...candidates.map((candidate) => candidate.score));
      const best = candidates.filter((candidate) => candidate.score === bestScore);
      if (best.length === 1) {
        productKey ??= best[0]?.product;
        componentKey ??= best[0]?.component;
      } else if (best.length === 0 && entry.key.indexOf('+') > 0 && entry.key.indexOf('+') === entry.key.lastIndexOf('+')) {
        const at = entry.key.indexOf('+');
        const product = entry.key.slice(0, at);
        const component = entry.key.slice(at + 1);
        if (product.includes(':V') && component.includes(':')
          && (productKey === undefined || productKey === product) && (componentKey === undefined || componentKey === component)) {
          productKey ??= product;
          componentKey ??= component;
        }
      }
    }
    return {
      product: entry.display?.product ?? (productKey === undefined ? undefined : resolve('ProduktVersion', productKey)),
      component: entry.display?.component ?? (componentKey === undefined ? undefined : resolve('KomponenteVersion', componentKey)),
    };
  }

  const byEntryId = new Map<string, EntryPresentation>();
  const groups = new Map<string, BomGroup & { components: BomComponent[] }>();
  for (const entry of entries) {
    const identity = resolve(entry.klass, entry.key);
    const changes = entry.changes.map((change) => presentChange(entry, change));
    byEntryId.set(entry.entry_id, { title: identity.name, version: identity.version ?? null, changes });
    if (entry.klass !== 'ProduktVersionKomponente') continue;

    const { product, component } = bomReferences(entry);
    const groupKey = product === undefined ? 'unresolved' : JSON.stringify(['product-version', product.key]);
    const group = groups.get(groupKey) ?? {
      key: groupKey,
      productName: product?.name ?? 'Nicht zugeordnete Stücklisteneinträge',
      productVersion: product?.version || null,
      components: [],
    };
    group.components.push({
      entry,
      componentName: component?.name ?? entry.key,
      componentVersion: component?.version || null,
      changes: entry.changes
        .filter((change) => entry.action !== 'create' || (change.field !== 'ProduktVersionID' && change.field !== 'KomponenteVersionID'))
        .map((change) => presentChange(entry, change)),
    });
    groups.set(groupKey, group);
  }
  return { byEntryId, bomGroups: [...groups.values()] };
}
